/**
 * @fileoverview Org portal — activity feed (Spec 11 WP3). Renders the org's
 * append-only audit ledger (GET /org/events) as human-readable sentences:
 * "Org Admin turned billing off for Jane K. · Jul 9, 3:12 PM".
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { RANGES } from "@/lib/orgReporting";
import { Card, Icon } from "@/components/studio/primitives";
import { ORG_FEATURES } from "@/lib/orgFeatures";

// Historical events retain the retired feature's original human-readable label.
const FEATURE_LABELS = { billing: "Billing & invoices", ...Object.fromEntries(ORG_FEATURES.map((f) => [f.key, f.label])) };

function featureDiff(detail) {
  const after = detail?.after || {};
  const parts = Object.entries(after).map(([k, v]) => {
    const label = FEATURE_LABELS[k] || k;
    if (v === null || v === undefined) return `reset ${label}`;
    return `turned ${label} ${v ? "on" : "off"}`;
  });
  return parts.join(", ") || "updated features";
}

/** One sentence per event kind. actor/subject names are resolved server-side. */
function describe(event) {
  const actor =
    event.actor_name ||
    (event.actor_type === "instructor" ? "An instructor" : event.actor_type === "org_viewer" ? "A viewer" : "An admin");
  const subject = event.subject_name || "an instructor";
  switch (event.kind) {
    case "branding_updated":
      return `${actor} updated the branding (${Object.keys(event.detail?.changed || {}).join(", ") || "no fields"})`;
    case "features_updated":
      return `${actor} ${featureDiff(event.detail)} for the whole organization`;
    case "instructor_features_updated":
      return `${actor} ${featureDiff(event.detail)} for ${subject}`;
    case "invite_sent":
      return `${actor} invited ${event.detail?.email || "an instructor"}`;
    case "invite_revoked":
      return `${actor} revoked the invitation for ${event.detail?.email || "an instructor"}`;
    case "invite_accepted":
      return `${subject} accepted the invitation and joined`;
    case "member_auto_enrolled":
      return `${subject} joined automatically on sign-up`;
    case "seat_assigned":
      return event.actor_type === "system"
        ? `${subject} was given a seat automatically on sign-up`
        : `${actor} gave ${subject} a seat`;
    case "seat_unassigned":
      return `${actor} removed ${subject}'s seat`;
    case "program_member_auto_added":
      return `${subject} was added to a program automatically on sign-up`;
    case "invite_declined":
      return `${subject} declined the invitation`;
    case "member_removed":
      return `${actor} removed ${subject} from the organization`;
    case "member_left":
      return `${subject} left the organization`;
    case "lessons_viewed":
      return `${actor} viewed lesson summaries for one of ${subject}'s students`;
    case "admin_created":
      return `${actor} added admin ${event.detail?.email || ""}`.trim();
    case "admin_deactivated":
      return `${actor} deactivated admin ${event.detail?.email || ""}`.trim();
    case "admin_reactivated":
      return `${actor} reactivated admin ${event.detail?.email || ""}`.trim();
    case "admin_deleted":
      return `${actor} deleted their administrator account`;
    case "admin_invite_resent":
      return `${actor} re-sent the admin invitation to ${event.detail?.email || "an admin"}`;
    case "viewer_created": {
      const who = event.detail?.email || "a viewer";
      if (event.detail?.invitation === "link") return `${actor} added viewer ${who} and made a link to share`;
      if (event.detail?.invitation === "email") return `${actor} added viewer ${who} and emailed their invitation`;
      return `${actor} added viewer ${who}`;
    }
    case "viewer_invite_resent":
      return `${actor} re-sent the viewer invitation to ${event.detail?.email || "a viewer"}`;
    case "viewer_invite_link_created":
      return `${actor} made a new invitation link for ${event.detail?.email || "a viewer"}`;
    case "viewer_left": {
      // A viewer removing themselves; from their last program it closes the account.
      const who = event.actor_name || event.detail?.email || "A viewer";
      const program = event.detail?.program_title || "a program";
      return event.detail?.account_closed
        ? `${who} stopped viewing ${program}, their last program, and closed their viewer account`
        : `${who} stopped viewing ${program}`;
    }
    case "announcement_sent": {
      const n = event.detail?.instructors_reached ?? 0;
      const what = event.detail?.kind === "class_announcement" ? "a class announcement" : "an announcement";
      return `${actor} sent ${what} to ${n} instructor${n === 1 ? "" : "s"}`;
    }
    case "meeting_cancelled":
      return `${actor} cancelled a class meeting on ${event.detail?.date || "a date"}`;
    case "notification_settings_updated":
      return `${actor} changed the instructor notification settings`;
    case "class_roster_imported":
      return `${actor} imported a class roster (${event.detail?.created ?? 0} new, ${event.detail?.matched ?? 0} matched)`;
    case "data_exported": {
      // The dashboard's Export: which tab, and over which range where one applies.
      const range = RANGES.find((r) => r.id === event.detail?.period);
      return `${actor} exported ${event.detail?.kind || "data"} as CSV${range ? ` (${range.label.toLowerCase()})` : ""}`;
    }
    default:
      return `${actor} — ${event.kind}`;
  }
}

const ICONS = {
  branding_updated: "sparkle",
  features_updated: "settings",
  instructor_features_updated: "settings",
  invite_sent: "mail",
  invite_revoked: "mail",
  invite_accepted: "users",
  invite_declined: "users",
  member_auto_enrolled: "users",
  seat_assigned: "shield",
  seat_unassigned: "shield",
  program_member_auto_added: "users",
  member_removed: "users",
  member_left: "users",
  lessons_viewed: "book",
  admin_created: "shield",
  admin_deactivated: "shield",
  admin_reactivated: "shield",
  admin_deleted: "shield",
  admin_invite_resent: "mail",
  viewer_created: "users",
  viewer_invite_resent: "mail",
  viewer_invite_link_created: "mail",
  viewer_left: "users",
  announcement_sent: "bell",
  meeting_cancelled: "calendar",
  data_exported: "download",
  notification_settings_updated: "bell",
  class_roster_imported: "users",
};

function when(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function OrgActivityPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await orgApi.get("/org/events?limit=200");
      setEvents(resp.data.events || []);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load activity"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h1 className="mb-2 font-serif text-2xl font-semibold text-[var(--text-primary)]">Activity</h1>
      <p className="mb-6 font-sans text-[13px] text-[var(--text-tertiary)]">
        Every administrative action in your organization, permanently recorded.
      </p>

      <Card padding={0}>
        {loading ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
        ) : events.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">No activity yet.</div>
        ) : (
          events.map((ev, i) => (
            <div
              key={ev.event_id}
              className={[
                "flex items-start gap-3 px-[18px] py-[13px]",
                i < events.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
              ].join(" ")}
            >
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--studio-interactive)]">
                <Icon name={ICONS[ev.kind] || "clock"} size={15} strokeWidth={1.7} className="text-[var(--text-secondary)]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-sans text-[15px] leading-snug text-[var(--text-primary)]">{describe(ev)}</div>
                <div className="mt-0.5 font-sans text-[12px] text-[var(--text-tertiary)]">{when(ev.created_at)}</div>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
